import {
  DataType,
  Form,
  FormOptions,
  Section,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Button, Card, CardHeader, Grid, makeStyles, useTheme } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import {
  EnumEstadoCiclo,
  EnumTipoAgrupacion,
  EnumTipoFacturacion,
  EnumTipoRefacturacion
} from '../../../api'
import { ReinvoiceRequestModel } from '../../../api/Argentia/endpoints/settlementBatchs/model/settlementBatchs'
import { SettlementTableModel } from '../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumDominioTarea } from '../../../api/oData/enums/enumDominioTarea'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { CardActionProps } from '../../../components/shared/cardActions/cardActions'
import { CardBottom } from '../../../components/shared/cardBottom/cardBottom'
import { ObjectionsDialog } from '../../../components/shared/objectionsDialog/objectionsDialog'
import { SectionLoader } from '../../../components/shared/sectionLoader/sectionLoader'
import { WarningBanner } from '../../../components/shared/warningBanner/warningBanner'
import { envConfig } from '../../../config/envConfig'
import { getUserPermissions, hasPermission } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Pages, Resources } from '../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../redux'
import { terminateObjection } from '../../../services/objections.services'
import { CycleExecutionModel } from '../../invoiceCycles/invoiceCycles.model'
import { fetchInvoiceCycleExecution } from '../../invoiceCycles/invoiceCycles.service'
import { ObjectionTableModel } from '../../invoicesPage/invoicesPage.Model'
import {
  fetchObjectionsByInvoiceID,
  getInvoicesByEmissionDate,
  saveObjection
} from '../../invoicesPage/invoicesPage.service'
import { createReinvoiceBatch } from '../../settlementBatchs/settlementBatchs.services'
import { CloseExecutionCycleDialog } from '../components/closeExecutionCycleDialog'
import { RegenerateExecutionCycleDialog } from '../components/regenerateExecutionCycleDialog/regenerateExecutionCycleDialog'
import { SearchFilterCard } from '../components/searchFilterCard/searchFilterCard'
import { SettlementsTableCard } from '../components/settlementsTableCard/settlementsTableCard'
import { TerminateObjectionsDialog } from '../components/terminateObjectionsDialog/terminateObjectionsDialog'
import {
  useCloseExecutionCycleDialog,
  useExecutionCycleActions,
  useNavigateToSettlementsInvoices
} from '../hooks'
import {
  completeDataTableActions,
  mapObjectionsResponseToTableModel,
  mapSettlementTableApiResponse
} from '../settlements.mapper'
import {
  ClosedSettlementsSearchRequest,
  EnumSettlementSearchType,
  EnumSettlementTableCustomActionConfig,
  SettlementFormSearchFilter
} from '../settlements.models'
import { getHasCancelledOrPendingSettlements } from '../settlements.service'
import { styles } from '../settlements.styles'

interface SearchFormData {
  startDate?: string
  endDate?: string
}

interface SettlementsAnnulationPageProps {
  location?: {
    state: {
      executionCycleId?: number
      invoiceCycleId?: number
      billingType?: EnumTipoFacturacion
      reinvoicingType: EnumTipoRefacturacion
      groupingType: EnumTipoAgrupacion
      finishedExecutionCycle?: boolean
      startDate?: string
      endDate?: string
    }
  }
}

export const SettlementsAnnulationPage: React.FC<SettlementsAnnulationPageProps> = props => {
  const history = useHistory()
  const executionCycleId = props.location?.state?.executionCycleId
  const invoiceCycleId = props.location?.state?.invoiceCycleId
  const billingType = props.location?.state?.billingType
  const isFinishedExecutionCyclePage = props.location?.state?.finishedExecutionCycle ?? false
  const initialStartDate = props.location?.state?.startDate
    ? moment(props.location.state.startDate).format('YYYY-MM-DD')
    : undefined
  const initialEndDate = props.location?.state?.endDate
    ? moment(props.location.state.endDate).format('YYYY-MM-DD')
    : undefined
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements.annulation'])
  const { error, info } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    startDate: initialStartDate,
    endDate: initialEndDate
  })
  const [tableData, setTableData] = useState<SettlementTableModel[]>([])
  const [showTable, setShowTable] = useState<boolean>(false)
  const [selectedSettlementsData, setSelectedSettlementsData] = useState<SettlementTableModel[]>([])
  const [showSearchFilter, setShowSearchFilter] = useState<boolean>(false)
  const [searchFilters, setSearchFilters] = useState<SettlementFormSearchFilter>({
    searchType: EnumSettlementSearchType.Contract,
    searchValue: ''
  })
  const [hasOpenOrPendingSettlements, setHasOpenOrPendingSettlements] = useState<boolean>(false)
  const [executionData, setExecutionData] = useState<CycleExecutionModel | null>(null)

  // Invoice objections dialog
  const [invoiceRow, setInvoiceRow] = useState<SettlementTableModel | null>(null)
  const [settlementInvoicesObjections, setSettlementInvoicesObjections] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)
  const [showObjectionsDialog, setShowObjectionsDialog] = useState<boolean>(false)
  const [isLoadingObjections, setIsLoadingObjections] = useState<boolean>(false)

  // Terminate objections dialog
  const [showTerminateObjectionsDialog, setShowTerminateObjectionsDialog] = useState<boolean>(false)
  const [terminateObjectionsRows, setTerminateObjectionsRows] = useState<SettlementTableModel[]>([])

  const buildSearchRequest = useCallback(
    (filters: SettlementFormSearchFilter): ClosedSettlementsSearchRequest => {
      const { searchType, searchValue, ...advancedFilters } = filters

      const searchValueField = searchValue
        ? ({
            [EnumSettlementSearchType.Contract]: { contractCode: searchValue },
            [EnumSettlementSearchType.CUPS]: { cupsCode: searchValue },
            [EnumSettlementSearchType.Subject]: { subjectName: searchValue },
            [EnumSettlementSearchType.InvoiceCode]: { invoiceCode: searchValue }
          }[searchType] ?? {})
        : {}

      return {
        startDate: searchForm.startDate!,
        endDate: searchForm.endDate!,
        maxResults: envConfig.invoiceCycleConfig.maxQueryItems,
        ...searchValueField,
        ...advancedFilters
      }
    },
    [searchForm.startDate, searchForm.endDate]
  )

  const {
    handleRegenerateExecutionCycle,
    closeExecutionCycle,
    regenerateDialogOpen,
    handleRegenerateDialogClose,
    regenerateInitialData
  } = useExecutionCycleActions({
    executionCycleId: executionCycleId ?? 0,
    invoiceCycleId: invoiceCycleId ?? 0,
    billingType,
    executionInitialData: {
      startDate: executionData?.startDate,
      endDate: executionData?.endDate,
      invoiceCycleSubtype: executionData?.subtype
    }
  })

  const { navigateToSettlementsInvoices } = useNavigateToSettlementsInvoices()

  const { isOpen, openDialog, closeDialog, onCloseWithInvoices, onCloseWithoutInvoices } =
    useCloseExecutionCycleDialog({ closeExecutionCycle })

  // Check if execution cycle has cancelled or pending settlements on mount
  useEffect(() => {
    const checkCycleStatus = async () => {
      if (!executionCycleId) return

      const response = await getHasCancelledOrPendingSettlements(executionCycleId)
      if (response.success && response.data) {
        setHasOpenOrPendingSettlements(response.data.hasCancelledOrPending)
      }
    }

    checkCycleStatus()

    if (executionCycleId) {
      fetchInvoiceCycleExecution(executionCycleId).then(res => {
        if (res.success && res.data) setExecutionData(res.data)
      })
    }

    // Also auto-search if dates are pre-filled from navigation
    if (initialStartDate && initialEndDate) {
      handleSearch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionCycleId]) // Intentionally runs once on mount

  // #region Handlers

  const handleSearch = useCallback(
    async (filtersOverride?: SettlementFormSearchFilter) => {
      if (!searchForm.startDate || !searchForm.endDate) {
        error(t('settlements.annulation:validations.datesRequired'))
        return
      }

      // When called from main search button (no override), clear any applied filters
      const activeFilters: SettlementFormSearchFilter = filtersOverride ?? {
        searchType: EnumSettlementSearchType.Contract,
        searchValue: ''
      }
      if (!filtersOverride) {
        setSearchFilters(activeFilters)
      }

      setIsFetching(true)
      setShowTable(false)
      setSelectedSettlementsData([])

      const request = buildSearchRequest(activeFilters)
      const response = await getInvoicesByEmissionDate(request)

      setIsFetching(false)

      if (response.success && response.data) {
        setTableData(completeDataTableActions(mapSettlementTableApiResponse(response.data)))
        setShowTable(true)
      } else {
        error(response.error?.message ?? t('settlements.annulation:notifications.fetchError'))
      }
    },
    [searchForm.startDate, searchForm.endDate, buildSearchRequest, error, t]
  )

  const handleSelectionChange = useCallback(
    (selectedRows: SettlementTableModel[], _selectedIndices: number[]) => {
      setSelectedSettlementsData(selectedRows)
    },
    []
  )

  const handleInvoiceSelected = useCallback(async () => {
    await getConfirmation(ConfirmationDialogs.INVOICE_CYCLE_CUSTOM_SELECTION).then(async () => {
      const request: ReinvoiceRequestModel = {
        invoiceIds: selectedSettlementsData.map(row => row.invoiceId!),
        cycleId: invoiceCycleId!,
        executionCycleId: executionCycleId!,
        reinvoicingType: executionData?.reInvoiceType,
        groupingType: executionData?.groupingType
      }

      const response = await createReinvoiceBatch(request)
      if (response.success) {
        history.push(NonAuthRoutes.invoiceCycleProcessing, {
          executionCycleId: executionCycleId,
          invoiceCycleId,
          billingType,
          originState: EnumEstadoCiclo.PdteValidarListado,
          startDate: searchForm.startDate,
          endDate: searchForm.endDate
        })
      } else {
        error(response.error?.message ?? t('settlements.annulation:notifications.batchError'))
      }

      closeConfirmation()
    })
  }, [
    getConfirmation,
    selectedSettlementsData,
    executionCycleId,
    invoiceCycleId,
    executionData,
    history,
    billingType,
    searchForm.startDate,
    searchForm.endDate,
    closeConfirmation
  ])

  const handleViewSearchCard = useCallback(() => {
    setShowSearchFilter(prev => !prev)
  }, [])

  const handleCancelSearch = useCallback(() => {
    setShowSearchFilter(false)
  }, [])

  const handleSearchFilterSubmit = useCallback(
    (_filters: SettlementFormSearchFilter) => {
      setSearchFilters(_filters)
      handleSearch(_filters)
    },
    [handleSearch]
  )

  const handleViewSettlementsInvoices = useCallback(async () => {
    await navigateToSettlementsInvoices({
      executionCycleId: executionCycleId ?? 0,
      invoiceCycleId: invoiceCycleId ?? 0,
      billingType,
      isFinishedExecutionCycle: isFinishedExecutionCyclePage
    })
  }, [
    navigateToSettlementsInvoices,
    executionCycleId,
    invoiceCycleId,
    billingType,
    isFinishedExecutionCyclePage
  ])

  // #region Objections Handlers

  const handleViewInvoiceObjections = useCallback(async (settlementRow: SettlementTableModel) => {
    setShowObjectionsDialog(true)
    setInvoiceRow(settlementRow)
    const response = await fetchObjectionsByInvoiceID(settlementRow.invoiceId!).then(res => {
      if (res.success && res.data) {
        return res.data
      } else {
        error(t('settlements.annulation:fetchResponse.fetchInvoiceObjections.error'))
        return []
      }
    })
    setSettlementInvoicesObjections(mapObjectionsResponseToTableModel(response))
  }, [])

  const handleObjectionsDialogClose = useCallback(
    (refreshData: boolean = false) => {
      setShowObjectionsDialog(false)
      setSettlementInvoicesObjections(undefined)
      if (refreshData) {
        handleSearch(searchFilters)
      }
    },
    [handleSearch, searchFilters]
  )

  const onSaveObjection = async (row: ObjectionTableModel, invoiceID: number) => {
    let refreshData: boolean = false
    await saveObjection(row, invoiceID)
      .then(response => {
        if (response.success) {
          refreshData = true
          info(t('settlements.annulation:fetchResponse.saveObjection.success'))
        } else {
          error(t('settlements.annulation:fetchResponse.saveObjection.error'))
        }
      })
      .finally(() => {
        handleObjectionsDialogClose(refreshData)
      })
  }

  const handleTerminateObjection = async (
    row: ObjectionTableModel,
    closeObjectionsDialog: boolean = false
  ) => {
    getConfirmation(ConfirmationDialogs.TERMINATE_OBJECTION).then(async () => {
      await terminateObjection(row.id!).then(response => {
        if (response.success) {
          info(t('settlements.annulation:fetchResponse.terminateObjection.success'))
        } else {
          error(t('settlements.annulation:fetchResponse.terminateObjection.error'))
        }
      })
      if (closeObjectionsDialog) {
        handleObjectionsDialogClose(true)
      }
      closeConfirmation()
    })
  }

  const refreshCurrentInvoiceObjections = useCallback(async () => {
    if (showObjectionsDialog && invoiceRow?.invoiceId) {
      setIsLoadingObjections(true)
      const res = await fetchObjectionsByInvoiceID(invoiceRow.invoiceId)
      if (res.success && res.data) {
        setSettlementInvoicesObjections(mapObjectionsResponseToTableModel(res.data))
      }
      setIsLoadingObjections(false)
    }
  }, [showObjectionsDialog, invoiceRow?.invoiceId])

  const handleTerminateObjectionsClick = useCallback((selectedRows: SettlementTableModel[]) => {
    setTerminateObjectionsRows(selectedRows)
    setShowTerminateObjectionsDialog(true)
  }, [])

  const handleRefreshInvoicesAfterTerminate = useCallback(() => {
    setTerminateObjectionsRows([])
    setSelectedSettlementsData([])
    handleSearch(searchFilters)
    refreshCurrentInvoiceObjections()
  }, [handleSearch, searchFilters, refreshCurrentInvoiceObjections])

  const handleRefreshObjectionsDialog = useCallback(() => {
    handleSearch(searchFilters)
    refreshCurrentInvoiceObjections()
  }, [handleSearch, searchFilters, refreshCurrentInvoiceObjections])

  // #endregion Objections Handlers

  // #endregion Handlers

  // #region Form Definition

  const formOptions: FormOptions = {
    columns: 2,
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlements.annulation:title')
    },
    elevation: 0
  }

  const formSections = useMemo<Section[]>(
    () => [
      {
        inputs: [
          {
            name: 'startDate',
            label: t('settlements.annulation:form.startDate'),
            type: DataType.DATE,
            required: true,
            onChange: value => setSearchForm(prev => ({ ...prev, startDate: value?.toString() }))
          },
          {
            name: 'endDate',
            label: t('settlements.annulation:form.endDate'),
            type: DataType.DATE,
            required: true,
            onChange: value => setSearchForm(prev => ({ ...prev, endDate: value?.toString() }))
          }
        ]
      }
    ],
    [t]
  )

  // #endregion Form Definition

  const cardFooterActions = useMemo<CardActionProps[]>(
    () => [
      {
        label: t('settlements.annulation:actions.advancedSearch'),
        handleActionClick: handleViewSearchCard,
        hasPermission: true,
        color: 'primary'
      },
      {
        label: `${t('settlements.annulation:actions.invoiceSelected')} ${selectedSettlementsData.length ? `(${selectedSettlementsData.length})` : ''}`,
        handleActionClick: handleInvoiceSelected,
        hasPermission: true,
        color: 'primary',
        disabled: selectedSettlementsData.length === 0
      }
    ],
    [
      selectedSettlementsData.length,
      handleInvoiceSelected,
      handleViewSearchCard,
      showSearchFilter,
      t
    ]
  )

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageHeader title={t('settlements.annulation:title')}>
        <Button
          variant="text"
          color="primary"
          onClick={() => history.push(NonAuthRoutes.invoiceCyclesNew)}
        >
          {t('settlements.annulation:header.buttons.goBack')}
        </Button>
        <Button
          variant="text"
          color="primary"
          onClick={handleViewSettlementsInvoices}
          disabled={!hasOpenOrPendingSettlements}
        >
          {t('settlements.annulation:header.buttons.viewSettlementsInvoices')}
        </Button>
        {!isFinishedExecutionCyclePage && (
          <>
            <Button variant="text" color="primary" onClick={handleRegenerateExecutionCycle}>
              {t('settlements.annulation:header.buttons.regenerate')}
            </Button>
            <Button variant="text" color="primary" onClick={openDialog}>
              {t('settlements.annulation:header.buttons.closeExecutionCycle')}
            </Button>
          </>
        )}
      </PageHeader>
      <PageCenterColumn>
        <Card className={classes.card}>
          <CardHeader title={t('settlements.annulation:card.title')} />
          <div className={classes.formStyleWrapper}>
            <div className={classes.formDialogContentCustom}>
              <Form sections={formSections} data={searchForm} options={formOptions} />
              <div className={classes.searchButtonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleSearch()}
                  disabled={isFetching || !searchForm.startDate || !searchForm.endDate}
                  startIcon={<SearchIcon />}
                >
                  {t('settlements.annulation:actions.search')}
                </Button>
              </div>
            </div>
          </div>
          <CardBottom actions={cardFooterActions} />
        </Card>

        <Grid container spacing={2}>
          <Grid item sm={12} md={showSearchFilter ? 5 : 12}>
            <SearchFilterCard
              showCard={showSearchFilter}
              filters={searchFilters}
              cycleState={EnumEstadoCiclo.PdteValidarListado}
              advancedSearch={true}
              onSearch={handleSearchFilterSubmit}
              onCancel={handleCancelSearch}
              billingType={billingType}
            />
          </Grid>
          <Grid item sm={12} md={showSearchFilter ? 7 : 12}>
            <SectionLoader
              show={isFetching && !showTable}
              message={t('settlements.annulation:searching')}
            />

            <WarningBanner
              show={showTable && tableData.length >= envConfig.invoiceCycleConfig.maxQueryItems}
              message={t('settlements.annulation:maxResultsWarning', {
                max: envConfig.invoiceCycleConfig.maxQueryItems
              })}
            />

            {showTable && (
              <SettlementsTableCard
                isSettlements={executionData?.status === EnumEstadoCiclo.PdteValidarListado}
                data={tableData}
                isLoading={isFetching}
                show={showTable}
                isSettlementsInvoice={executionData?.status === EnumEstadoCiclo.PdteValidarFacturas}
                billingType={billingType}
                canDownload={false}
                customToolbarActionsConfig={EnumSettlementTableCustomActionConfig.CUSTOM_SELECTION}
                onSelectionChange={handleSelectionChange}
                onViewObjectionsClick={handleViewInvoiceObjections}
                onTerminateObjectionsClick={handleTerminateObjectionsClick}
                // qualityBad proviene de datos de liquidaciones; al usar el endpoint de facturas
                // (apiGetInvoicesByEmissionDate) este campo no está disponible. Ocultar hasta
                // que el endpoint de anulación devuelva dicho dato.
                hideQualityBad
                // No hay id de liquidación al usar datos de facturas; usar invoiceId como identificador de fila
                rowIdField="invoiceId"
              />
            )}
          </Grid>
        </Grid>
      </PageCenterColumn>

      <CloseExecutionCycleDialog
        open={isOpen}
        onClose={closeDialog}
        executionCycleId={executionCycleId ?? 0}
        invoiceCycleId={invoiceCycleId ?? 0}
        billingType={billingType}
        onCloseWithoutInvoiceGeneration={onCloseWithoutInvoices}
        onCloseWithInvoiceGeneration={onCloseWithInvoices}
      />

      <RegenerateExecutionCycleDialog
        open={regenerateDialogOpen}
        onClose={handleRegenerateDialogClose}
        initialData={regenerateInitialData}
      />

      <ObjectionsDialog
        open={showObjectionsDialog}
        handleClose={() => handleObjectionsDialogClose(false)}
        dataTableTitle={t('settlements.annulation:dialogs.invoicesObjections.title')}
        showDescription
        taskDomain={EnumDominioTarea.Factura}
        objectionsData={isLoadingObjections ? undefined : settlementInvoicesObjections}
        tableUserPermissions={getUserPermissions(Pages.InvoicesPage)}
        hasEditPermission={hasPermission(Resources.InvoicesPage, [UserPermission.EDIT])}
        allowInsert
        onRowAdd={row => onSaveObjection(row, invoiceRow?.invoiceId!)}
        onTerminateObjection={row => handleTerminateObjection(row, true)}
        canBulkTerminate
        canModifyCriticality
        onRefresh={handleRefreshObjectionsDialog}
      />

      <TerminateObjectionsDialog
        open={showTerminateObjectionsDialog}
        handleClose={() => setShowTerminateObjectionsDialog(false)}
        selectedRows={terminateObjectionsRows}
        domain={EnumDominioTarea.Factura}
        entityIdField="invoiceId"
        onSuccess={handleRefreshInvoicesAfterTerminate}
      />
    </Page>
  )
}
